import React, { useContext } from 'react';
import { Form, Formik } from 'formik';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import classNames from 'classnames';
import * as Yup from 'yup';
import { useNotificationQueue } from 'components/notification';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import {
  MUTATION_ADD_PERSON_INJURY,
  MUTATION_EDIT_PERSON_INJURY,
  QUERY_GET_PERSON_MEDICALS
} from 'services/aws/injury-query';
import { ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL } from 'routes/RouteList';
import { StoreContext } from 'index';
import { useMedicalContext } from 'contexts/MedicalContext';
import messages from 'messages';
import { parseDateString } from 'utils/date';
import { format } from 'date-fns';
import { useEntityContext } from 'containers/pages/persons/store/useEntityContext';

function InjuryForm({ personInjury, extraClassNames, onComplete, children }) {
  const notification = useNotificationQueue();
  const intl = useIntl();
  const {
    authStore: {
      user: { rootEntityId }
    }
  } = useContext(StoreContext);

  const { push } = useHistory();
  const { entityId, groupId } = useParams();

  const person = useEntityContext(s => s.person);
  const { medical } = useMedicalContext();

  const [addInjury] = useMutation(MUTATION_ADD_PERSON_INJURY);

  const [editInjury] = useMutation(MUTATION_EDIT_PERSON_INJURY);

  const onSubmitHandler = async personInjury => {
    const sides = Object.keys(personInjury.sides)
      .map(side => (personInjury.sides[side] ? side : null))
      .filter(s => s !== 'center' && s !== null);

    const injuryObject = {
      sides: sides,
      medicalId: medical.id,
      injuryId: personInjury.injury.id,
      injuryDate: format(personInjury.injuryDate, 'yyyy-LL-dd'),
      injuryEndDate: personInjury.injuryEndDate
        ? format(personInjury.injuryEndDate, 'yyyy-LL-dd')
        : null,
      meta: JSON.stringify({
        typeOfInjury: personInjury.typeOfInjury,
        causeOfInjury: personInjury.causeOfInjury,
        relatedTo: {
          sport: personInjury.relatedToSport,
          growth: personInjury.relatedToGrowth
        },
        description: personInjury.description,
        extraInfo: personInjury.extraInfo
      })
    };

    if (personInjury.id) {
      await editInjury({
        variables: { ...personInjury, ...injuryObject },
        refetchQueries: [
          {
            query: QUERY_GET_PERSON_MEDICALS,
            variables: { entityId: rootEntityId, personId: person.id }
          }
        ]
      }).then(res => {
        notification.add(res.data.editPersonInjury.id, {
          message: intl.formatMessage(messages.rehabInjurySaved)
        });
        if (onComplete) onComplete();
      });
    } else {
      await addInjury({
        variables: injuryObject,
        refetchQueries: [
          {
            query: QUERY_GET_PERSON_MEDICALS,
            variables: { entityId: rootEntityId, personId: person.id }
          }
        ]
      }).then(res => {
        notification.add(res.data.addPersonInjury.id, {
          message: intl.formatMessage(messages.rehabInjurySaved)
        });
        if (onComplete) onComplete();
        push(
          generatePath(ROUTE_PERSONS_ENTITY_PERSON_MEDICAL_INJURY_DETAIL, {
            entityId,
            groupId,
            personId: person.id,
            medicalId: medical.id,
            injuryId: res.data.addPersonInjury.id
          })
        );
      });
    }
  };

  const validationSchema = Yup.object().shape({
    injury: Yup.object().shape({
      id: Yup.string().required(intl.formatMessage(messages.injuryRequired))
    }),
    injuryDate: Yup.date()
      .transform(parseDateString)
      .typeError(intl.formatMessage(messages.dateRequired))
      .required(intl.formatMessage(messages.dateRequired)),
    injuryEndDate: Yup.date()
      .transform(parseDateString)
      .nullable()
      .default(null),
    sides: Yup.object().test(
      'at-least-one-side',
      intl.formatMessage(messages.sideRequired),
      value => {
        return value?.left || value?.right || value?.center;
      }
    )
  });

  /*
  sides: Yup.array()
      .of(
        Yup.object().shape({
          left: Yup.string(),
          checked: Yup.boolean()
        })
      )
      .compact(v => !v.checked)
      .matches(/(left|right|center)/)
      .required(intl.formatMessage(messages.sideRequired))
   */

  return (
    <Formik
      initialValues={personInjury}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {props => {
        return (
          <Form className={classNames(extraClassNames)} noValidate>
            {typeof children === 'function' ? children(props) : children}
          </Form>
        );
      }}
    </Formik>
  );
}

export default InjuryForm;
